import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, AlertTriangle, CheckCircle2, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { JOB_TYPE_LABELS, type JobType } from "@/hooks/usePrinters";

const ALL_JOB_TYPES: JobType[] = [
  "conference_bill",
  "customer_receipt",
  "cash_withdrawal",
  "cash_supply",
  "cash_register_close",
  "order_production",
];

interface PrinterStatus {
  totalPrinters: number;
  activePrinters: number;
  configuredJobTypes: JobType[];
  missingJobTypes: JobType[];
}

export function PrinterStatusCard() {
  const { activeRestaurant } = useRestaurantContext();
  const [status, setStatus] = useState<PrinterStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStatus() {
      if (!activeRestaurant) {
        setIsLoading(false);
        return;
      }

      try {
        // Get printers
        const { data: printers } = await supabase
          .from("printers")
          .select("id, is_active")
          .eq("restaurant_id", activeRestaurant.id);

        // Get job types
        const printerIds = printers?.map((p) => p.id) || [];
        let configuredJobTypes: JobType[] = [];

        if (printerIds.length > 0) {
          const { data: jobTypes } = await supabase
            .from("printer_job_types")
            .select("job_type")
            .in("printer_id", printerIds)
            .eq("is_enabled", true);

          configuredJobTypes = [...new Set(jobTypes?.map((jt) => jt.job_type as JobType) || [])];
        }

        const missingJobTypes = ALL_JOB_TYPES.filter(
          (jt) => !configuredJobTypes.includes(jt)
        );

        setStatus({
          totalPrinters: printers?.length || 0,
          activePrinters: printers?.filter((p) => p.is_active).length || 0,
          configuredJobTypes,
          missingJobTypes,
        });
      } catch (error) {
        console.error("Error fetching printer status:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStatus();
  }, [activeRestaurant]);

  if (isLoading) {
    return null;
  }

  if (!status) {
    return null;
  }

  const hasWarnings = status.totalPrinters === 0 || status.missingJobTypes.length > 0;
  const isFullyConfigured = status.totalPrinters > 0 && status.missingJobTypes.length === 0;

  return (
    <Card className={hasWarnings ? "border-yellow-500/50" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Printer className="h-4 w-4" />
          Impressoras
          {hasWarnings && (
            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Atenção
            </Badge>
          )}
          {isFullyConfigured && (
            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              OK
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Impressoras ativas</span>
          <span className="font-medium">
            {status.activePrinters} / {status.totalPrinters}
          </span>
        </div>

        {status.totalPrinters === 0 ? (
          <div className="text-sm text-yellow-600 bg-yellow-500/10 rounded-md p-2">
            Nenhuma impressora configurada. Configure para habilitar impressões automáticas.
          </div>
        ) : status.missingJobTypes.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Tipos de impressão sem impressora:
            </p>
            <div className="flex flex-wrap gap-1">
              {status.missingJobTypes.slice(0, 3).map((jt) => (
                <Badge key={jt} variant="secondary" className="text-xs">
                  {JOB_TYPE_LABELS[jt]}
                </Badge>
              ))}
              {status.missingJobTypes.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{status.missingJobTypes.length - 3}
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-green-600">
            Todos os tipos de impressão configurados
          </div>
        )}

        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link to="/dashboard/impressoras">
            <Settings className="h-4 w-4 mr-2" />
            Configurar Impressoras
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
